import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserPlus, Search, Mail, Loader2, ChevronDown, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_TABS = [["all", "All"], ["Invited", "Invited"], ["Active", "Active"], ["Suspended", "Suspended"], ["Archived", "Archived"]];
const blankInvite = {
  full_name: "", email: "", role: "", additional_roles: [], reporting_manager_id: "",
  notification_email: "", whatsapp: "", timezone: "Asia/Kolkata", working_days: [],
};

function initialsOf(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}
function inviteBadge(s) {
  const map = { created: "Invite created", sent: "Invite sent", accepted: "Accepted", expired: "Expired", cancelled: "Cancelled" };
  return map[s] || "";
}

export default function Users() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [inv, setInv] = useState(blankInvite);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setMembers(await apiFetch("/members")); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiFetch("/roles").then((r) => { setRoles(r); setInv((v) => ({ ...v, role: v.role || (r.find((x) => x.name === "Content Writer")?.name || r[0]?.name || "") })); }).catch(() => {}); }, []);

  const filtered = members.filter((m) => {
    const matchesQ = ((m.full_name || "") + (m.login_email || "") + (m.role || "")).toLowerCase().includes(q.toLowerCase());
    const matchesTab = tab === "all" || m.status === tab;
    return matchesQ && matchesTab;
  });

  const sendInvite = async () => {
    if (!inv.email.trim()) return toast.error("Please enter an email address.");
    if (!inv.role) return toast.error("Please choose a primary role.");
    setBusy(true);
    try {
      await apiFetch("/auth/invite", { method: "POST", body: JSON.stringify({ ...inv, email: inv.email.trim() }) });
      toast.success("Invitation sent", { description: `${inv.email} will receive an email to set their password and join.` });
      setOpen(false); setMore(false); setInv({ ...blankInvite, role: inv.role });
      await load();
    } catch (e) { toast.error("Could not send invitation", { description: e.message }); }
    finally { setBusy(false); }
  };

  const act = async (path, method = "POST", body) => {
    try { await apiFetch(path, { method, body: body ? JSON.stringify(body) : undefined }); toast.success("Done"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader eyebrow="Team" title="Members"
        subtitle="Invite team members and manage their access. Accounts are invite-only via Supabase — passwords are set by the member and never stored here."
        actions={<Button className="gap-2" data-testid="invite-user-button" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> Invite Member</Button>} />

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="member-status-tabs">
        {STATUS_TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`status-tab-${k.toLowerCase()}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${tab === k ? "border-accent bg-accent-subtle text-foreground" : "border-border text-muted-foreground hover:bg-muted/50"}`}>
            {l}<span className="ml-1 text-[10px] text-accent">{k === "all" ? members.length : members.filter((m) => m.status === k).length || ""}</span>
          </button>
        ))}
        <div className="relative ml-auto max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="member-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border py-16 text-center text-sm text-muted-foreground" data-testid="members-empty">No members found. Invite your team to get started.</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border" data-testid="members-table">
          {filtered.map((m) => (
            <div key={m.id} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-0 hover:bg-muted/30" data-testid={`member-row-${m.id}`}>
              <button className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => navigate(`/team/members/${m.id}`)}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-primary">{initialsOf(m.full_name)}</span>
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{m.full_name || "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">{m.login_email}</div>
                </div>
              </button>
              <span className="hidden w-40 truncate text-sm text-foreground sm:block">{m.role || "—"}</span>
              <div className="flex w-28 items-center gap-1">
                <StatusBadge status={m.status} />
              </div>
              {m.status === "Invited" && m.invite_state && <span className="hidden text-[11px] text-muted-foreground md:block">{inviteBadge(m.invite_state)}</span>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded p-1.5 text-muted-foreground hover:text-foreground" data-testid={`member-actions-${m.id}`}><MoreVertical className="h-4 w-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate(`/team/members/${m.id}`)} data-testid={`action-edit-${m.id}`}>Edit profile & role</DropdownMenuItem>
                  {m.status === "Invited" && <DropdownMenuItem onClick={() => act(`/members/${m.id}/resend-invite`)} data-testid={`action-resend-${m.id}`}>Resend invitation</DropdownMenuItem>}
                  {m.status === "Invited" && <DropdownMenuItem onClick={() => act(`/members/${m.id}/cancel-invite`)} data-testid={`action-cancel-${m.id}`}>Cancel invitation</DropdownMenuItem>}
                  {m.status === "Active" && <DropdownMenuItem onClick={() => act(`/members/${m.id}/suspend`, "PUT", { active: false })} data-testid={`action-suspend-${m.id}`}>Deactivate (suspend)</DropdownMenuItem>}
                  {m.status === "Suspended" && <DropdownMenuItem onClick={() => act(`/members/${m.id}/suspend`, "PUT", { active: true })} data-testid={`action-activate-${m.id}`}>Reactivate</DropdownMenuItem>}
                  {m.status !== "Archived" ? (
                    <DropdownMenuItem onClick={() => act(`/members/${m.id}/archive`, "PUT", { archived: true })} data-testid={`action-archive-${m.id}`}>Archive</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => act(`/members/${m.id}/archive`, "PUT", { archived: false })} data-testid={`action-restore-${m.id}`}>Restore</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {/* invite dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setMore(false); }}>
        <DialogContent data-testid="invite-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Invite a member</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1 py-1">
            <div><Label className="text-xs">Full name</Label><Input value={inv.full_name} onChange={(e) => setInv({ ...inv, full_name: e.target.value })} placeholder="Anaya Sharma" data-testid="invite-name" /></div>
            <div><Label className="text-xs">Login email</Label><Input type="email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} placeholder="anaya@nirvana.example" data-testid="invite-email" />
              <p className="mt-1 text-[11px] text-muted-foreground">An invitation link is emailed. The member sets their own password — nothing is stored here.</p></div>
            <div><Label className="text-xs">Primary role</Label>
              <Select value={inv.role} onValueChange={(v) => setInv({ ...inv, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue placeholder="Choose a role" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Collapsible open={more} onOpenChange={setMore}>
              <CollapsibleTrigger asChild><Button variant="ghost" size="sm" className="gap-1.5 px-0 text-muted-foreground" data-testid="invite-more"><ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} /> More options</Button></CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                <div><Label className="text-xs">Additional roles</Label>
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="invite-additional-roles">
                    {roles.filter((r) => r.name !== inv.role).map((r) => (
                      <button key={r.name} type="button" onClick={() => setInv((v) => ({ ...v, additional_roles: v.additional_roles.includes(r.name) ? v.additional_roles.filter((x) => x !== r.name) : [...v.additional_roles, r.name] }))}
                        className={`rounded-full border px-2 py-0.5 text-[11px] ${inv.additional_roles.includes(r.name) ? "border-accent bg-accent-subtle text-accent" : "border-border text-muted-foreground"}`}>{r.name}</button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs">Reporting manager</Label>
                  <Select value={inv.reporting_manager_id || "none"} onValueChange={(v) => setInv({ ...inv, reporting_manager_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="invite-manager"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{members.filter((m) => m.status === "Active").map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.login_email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Notification email (if different)</Label><Input type="email" value={inv.notification_email} onChange={(e) => setInv({ ...inv, notification_email: e.target.value })} data-testid="invite-notif-email" /></div>
                  <div><Label className="text-xs">WhatsApp / mobile</Label><Input value={inv.whatsapp} onChange={(e) => setInv({ ...inv, whatsapp: e.target.value })} placeholder="+91…" data-testid="invite-whatsapp" /></div>
                </div>
                <div><Label className="text-xs">Timezone</Label><Input value={inv.timezone} onChange={(e) => setInv({ ...inv, timezone: e.target.value })} data-testid="invite-timezone" /></div>
                <div><Label className="text-xs">Working days</Label>
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="invite-working-days">
                    {WEEKDAYS.map((d) => (
                      <button key={d} type="button" onClick={() => setInv((v) => ({ ...v, working_days: v.working_days.includes(d) ? v.working_days.filter((x) => x !== d) : [...v.working_days, d] }))}
                        className={`rounded-full border px-2 py-0.5 text-[11px] ${inv.working_days.includes(d) ? "border-accent bg-accent-subtle text-accent" : "border-border text-muted-foreground"}`}>{d}</button>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={sendInvite} disabled={busy} className="gap-2" data-testid="send-invite"><Mail className="h-4 w-4" /> {busy ? "Sending…" : "Send Invitation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
